import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  Font,
  StyleSheet,
} from '@react-pdf/renderer';

// Long unbroken codes like "ATR-BKR-BCH-220X150" have no spaces, so react-pdf's
// default word-wrapper treats them as a single unbreakable token and lets them
// overflow into neighbouring columns. Teaching it to also break on hyphens
// (keeping the hyphen attached to the preceding fragment) fixes that overlap.
Font.registerHyphenationCallback((word: string) => {
  if (!word.includes('-') || word.length <= 1) return [word];
  const parts = word.split('-');
  return parts.map((p, i) => (i < parts.length - 1 ? `${p}-` : p)).filter(Boolean);
});

// ─── Types (mirror the Purchase Order shape from the PO details page) ───────
interface InspectorRef { id?: string; name?: string | null; }
interface AssignmentItem {
  inspection_type?: string | null;
  inspector?: InspectorRef | null;
  updated_at?: string | null;
}
interface CompletedInspectionItem {
  inspector_id?: string | null;
  inspector_name?: string | null;
}
interface LastInspection {
  inspector_name?: string | null;
  completed_at?: string | null;
  result?: string | null;
}
interface SkuItem {
  id: string;
  sku_number?: string | null;
  description?: string | null;
  product_category?: string | null;
  vendor_code?: string | null;
  color?: string | null;
  size?: string | null;
  qty_per_pack?: number | null;
  total_qty?: number | null;
  image_url?: string | null;
  assignments?: AssignmentItem[];
  last_inspection?: LastInspection | null;
  completed_inspections?: CompletedInspectionItem[];
}

export interface PurchaseOrderPDFData {
  id: string;
  po_number: string;
  work_order?: string | null;
  customer_name: string;
  ship_date?: string | null;
  skus?: SkuItem[];
  assignments?: AssignmentItem[];
  completed_inspections?: CompletedInspectionItem[];
  inspection_statuses?: Record<string, string>;
}

// Same people as the dashboard's "Assigned To": current inspectors (latest
// assigned first), then those who completed an inspection, since finalising
// deletes the assignment.
const getAssignedToText = (assignments: AssignmentItem[] = [], completed: CompletedInspectionItem[] = []) => {
  const names = new Map<string, string>();
  [...assignments]
    .sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''))
    .forEach(a => {
      const name = a.inspector?.name;
      const key = a.inspector?.id || name;
      if (name && key && !names.has(key)) names.set(key, name);
    });
  completed.forEach(ci => {
    const key = ci.inspector_id || ci.inspector_name;
    if (ci.inspector_name && key && !names.has(key)) names.set(key, `${ci.inspector_name} (Completed)`);
  });
  return names.size > 0 ? Array.from(names.values()).join(', ') : 'Unassigned';
};

// Keys match the "Columns" toggle on the PO Details page's SKU table
// (po-details-sku-visible-columns), minus the checkbox/Actions columns
// which don't exist in the PDF.
const SKU_PDF_COLUMN_FLEX = {
  sku_number: 1.35,
  image: 0.75,
  category: 1.2,
  vendor: 1.05,
  description: 1.75,
  assigned_to: 1.3,
  inspected_by: 1.2,
  inspection_date: 1.15,
  packs: 0.65,
  qty_per_pack: 0.75,
  total_qty: 0.85,
} as const;
type SkuPdfColumnKey = keyof typeof SKU_PDF_COLUMN_FLEX;
const SKU_PDF_COLUMN_KEYS = Object.keys(SKU_PDF_COLUMN_FLEX) as SkuPdfColumnKey[];

// ─── Helpers ──────────────────────────────────────────────────────────────
const dash = (v: any): string => {
  if (v === null || v === undefined) return '—';
  const str = String(v).trim();
  return str.length === 0 ? '—' : str;
};

const formatDate = (raw?: string | null) => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (raw?: string | null) => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const INSPECTION_TYPES: { key: string; label: string }[] = [
  { key: 'pilot', label: 'Pilot Inspection' },
  { key: 'fabric', label: 'Fabric Inspection' },
  { key: 'line', label: 'Line Inspection' },
  { key: 'final', label: 'Final Inspection' },
  { key: 'container', label: 'Container Inspection' },
];

const inspectionStatusColor = (status: string) => {
  switch (status) {
    case 'Completed':
    case 'Passed': return '#059669';
    case 'In Progress': return '#d97706';
    case 'Scheduled': return '#2563eb';
    case 'Failed': return '#dc2626';
    default: return '#94a3b8';
  }
};

// ─── Styles ───────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#1e293b',
    backgroundColor: '#ffffff',
    paddingHorizontal: 36,
    paddingTop: 20,
    paddingBottom: 60,
  },

  // Fixed page header
  topHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 },
  logoText: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: '#136bce' },
  logoSub: { fontSize: 6.5, color: '#94a3b8', letterSpacing: 1.5, marginTop: 1 },
  titleArea: { alignItems: 'flex-end' },
  titleLine1: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: '#1e293b' },
  titleLine2: { fontSize: 7.5, color: '#94a3b8', marginTop: 1 },
  hr: { borderBottomWidth: 2, borderBottomColor: '#136bce', marginBottom: 14 },

  // Hero banner
  hero: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#0e3a66',
    borderRadius: 12,
    paddingHorizontal: 20,
    paddingVertical: 18,
    marginBottom: 16,
  },
  heroLabel: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#93c5fd', letterSpacing: 2 },
  heroPoNumber: { fontSize: 22, fontFamily: 'Helvetica-Bold', color: '#ffffff', marginTop: 3 },
  heroCustomer: { fontSize: 9.5, color: '#cbd5e1', marginTop: 2 },
  heroRight: { alignItems: 'flex-end' },
  heroShipLabel: { fontSize: 7, color: '#93c5fd', letterSpacing: 1 },
  heroShipDate: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: '#ffffff', marginTop: 1 },

  // Gray section bar
  sectionHeader: {
    flexDirection: 'row', alignItems: 'center',
    borderBottomWidth: 1.5, borderBottomColor: '#136bce',
    paddingBottom: 5, marginBottom: 10, marginTop: 4,
  },
  sectionHeaderText: { fontSize: 10.5, fontFamily: 'Helvetica-Bold', color: '#0e3a66' },
  sectionHeaderBadge: {
    marginLeft: 6, backgroundColor: '#eff6ff', borderRadius: 8,
    paddingHorizontal: 7, paddingVertical: 2,
  },
  sectionHeaderBadgeText: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#136bce' },

  // Info grid (card style)
  card: { borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 10, backgroundColor: '#ffffff', marginBottom: 16, padding: 16 },
  infoGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  infoCell: { width: '33.33%', paddingRight: 10, marginBottom: 12 },
  infoLabel: { fontSize: 6.5, fontFamily: 'Helvetica-Bold', color: '#94a3b8', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.5 },
  infoValue: { fontSize: 9.5, fontFamily: 'Helvetica-Bold', color: '#1e293b' },

  // Inspection status grid
  statusGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  statusBox: { width: '18.6%', borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 8, backgroundColor: '#f8fafc', padding: 9 },
  statusBoxLabel: { fontSize: 6, fontFamily: 'Helvetica-Bold', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6, letterSpacing: 0.3 },
  statusBoxRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  statusDot: { width: 6, height: 6, borderRadius: 3 },
  statusBoxValue: { fontSize: 8, fontFamily: 'Helvetica-Bold' },

  // SKU table — columns use flex ratios (not width %) so padding never pushes
  // a cell past its allotted share of the row and overlaps its neighbour.
  table: { borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 6, marginBottom: 10, overflow: 'hidden' },
  tableHead: { flexDirection: 'row', backgroundColor: '#0e3a66' },
  tableRow: { flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: '#e2e8f0' },
  tableRowAlt: { backgroundColor: '#f8fafc' },
  th: { fontSize: 5.8, fontFamily: 'Helvetica-Bold', color: '#ffffff', paddingHorizontal: 4, paddingVertical: 6, textTransform: 'uppercase' },
  td: { fontSize: 6.8, color: '#334155', paddingHorizontal: 4, paddingVertical: 6 },
  tdBold: { fontSize: 6.8, fontFamily: 'Helvetica-Bold', color: '#1e293b', paddingHorizontal: 4, paddingVertical: 6 },
  tdMuted: { fontSize: 6.8, color: '#94a3b8', paddingHorizontal: 4, paddingVertical: 6, fontStyle: 'italic' },
  tdImgCell: { paddingHorizontal: 4, paddingVertical: 4, justifyContent: 'center', alignItems: 'center' },

  colImage: { flex: 0.75 },
  colSku: { flex: 1.35 },
  colCategory: { flex: 1.2 },
  colVendor: { flex: 1.05 },
  colDesc: { flex: 1.75 },
  colAssigned: { flex: 1.3 },
  colInspected: { flex: 1.2 },
  colDate: { flex: 1.15 },
  colPacks: { flex: 0.65, textAlign: 'right' },
  colQtyPack: { flex: 0.75, textAlign: 'right' },
  colQty: { flex: 0.85, textAlign: 'right' },

  thumb: { width: 22, height: 22, borderRadius: 4, objectFit: 'cover' },
  thumbPlaceholder: { width: 22, height: 22, borderRadius: 4, backgroundColor: '#e2e8f0', borderWidth: 0.5, borderColor: '#cbd5e1' },

  totalsRow: { flexDirection: 'row', backgroundColor: '#eff6ff', borderTopWidth: 1, borderTopColor: '#bfdbfe' },
  totalsLabel: { flex: 9.75, fontSize: 6.8, fontFamily: 'Helvetica-Bold', color: '#1d4ed8', paddingHorizontal: 4, paddingVertical: 6 },

  noDataText: { fontSize: 8.5, color: '#94a3b8', fontStyle: 'italic', paddingVertical: 10, textAlign: 'center' },

  // Footer
  pageFooter: {
    position: 'absolute', bottom: 14, left: 36, right: 36,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    borderTopWidth: 0.5, borderTopColor: '#e2e8f0', paddingTop: 5,
  },
  footerSide: { fontSize: 7, color: '#94a3b8', flex: 1 },
  footerCenter: { fontSize: 7, color: '#94a3b8', textAlign: 'center', flex: 1 },
  footerRight: { fontSize: 7, color: '#136bce', fontFamily: 'Helvetica-Bold', textAlign: 'right', flex: 1 },
});

// ─── Shared sub-components ──────────────────────────────────────────────────
const PageTop = () => (
  <>
    <View style={s.topHeader} fixed>
      <View>
        <Text style={s.logoText}>QA MATE</Text>
        <Text style={s.logoSub}>AI TEXTILE INSPECTOR</Text>
      </View>
      <View style={s.titleArea}>
        <Text style={s.titleLine1}>PURCHASE ORDER REPORT</Text>
        <Text style={s.titleLine2}>Generated {formatDateTime(new Date().toISOString())}</Text>
      </View>
    </View>
    <View style={s.hr} fixed />
  </>
);

const PageFooter = () => (
  <View style={s.pageFooter} fixed>
    <Text style={s.footerSide} />
    <Text
      style={s.footerCenter}
      render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
    />
    <Text style={s.footerRight}>QAMATE</Text>
  </View>
);

const SectionBar = ({ title, badge }: { title: string; badge?: string }) => (
  <View style={s.sectionHeader} wrap={false}>
    <Text style={s.sectionHeaderText}>{title}</Text>
    {badge ? (
      <View style={s.sectionHeaderBadge}>
        <Text style={s.sectionHeaderBadgeText}>{badge}</Text>
      </View>
    ) : null}
  </View>
);

const InfoCell = ({ label, value }: { label: string; value: string }) => (
  <View style={s.infoCell}>
    <Text style={s.infoLabel}>{label}</Text>
    <Text style={s.infoValue}>{value}</Text>
  </View>
);

// ─── Main PDF Document ───────────────────────────────────────────────────────
const PurchaseOrderPDF = ({ po, visibleSkuColumns }: { po: PurchaseOrderPDFData; visibleSkuColumns?: Record<string, boolean> }) => {
  const skus = po.skus || [];
  const assignments = po.assignments || [];

  const vc: Record<SkuPdfColumnKey, boolean> = SKU_PDF_COLUMN_KEYS.reduce((acc, key) => {
    acc[key] = visibleSkuColumns ? !!visibleSkuColumns[key] : true;
    return acc;
  }, {} as Record<SkuPdfColumnKey, boolean>);
  const totalsLabelFlex = SKU_PDF_COLUMN_KEYS
    .filter(k => k !== 'packs' && k !== 'qty_per_pack' && k !== 'total_qty' && vc[k])
    .reduce((sum, k) => sum + SKU_PDF_COLUMN_FLEX[k], 0);

  const assignedToText = getAssignedToText(assignments, po.completed_inspections);

  const inspectionStatuses = INSPECTION_TYPES.map(t => ({
    ...t,
    status: po.inspection_statuses?.[t.key] || 'Pending',
  }));

  const getSkuAssignedTo = (sku: SkuItem) => getAssignedToText(sku.assignments, sku.completed_inspections);

  const totalPacks = skus.reduce((sum, sk) => sum + ((sk.qty_per_pack || 0) > 0 ? (sk.total_qty || 0) / (sk.qty_per_pack || 1) : 0), 0);
  const totalUnits = skus.reduce((sum, sk) => sum + (sk.total_qty || 0), 0);
  const formatPacks = (n: number) => n.toFixed(2).replace(/\.?0+$/, '');

  return (
    <Document title={`Purchase Order - ${po.po_number}`}>
      {/* A single auto-flowing page: content paginates naturally instead of
          being forced onto separate pages (which left large blank gaps when
          a section was short). The fixed header/footer repeat on every
          physical page react-pdf generates as the content overflows. */}
      <Page size="A4" style={s.page} wrap>
        <PageTop />

        {/* Hero banner */}
        <View style={s.hero} wrap={false}>
          <View>
            <Text style={s.heroLabel}>PURCHASE ORDER</Text>
            <Text style={s.heroPoNumber}>{po.po_number}</Text>
            <Text style={s.heroCustomer}>{po.customer_name}</Text>
          </View>
          <View style={s.heroRight}>
            <Text style={s.heroShipLabel}>SHIP DATE</Text>
            <Text style={s.heroShipDate}>{formatDate(po.ship_date)}</Text>
          </View>
        </View>

        {/* Basic Details */}
        <SectionBar title="BASIC DETAILS" />
        <View style={s.card} wrap={false}>
          <View style={s.infoGrid}>
            <InfoCell label="Purchase Order Number" value={dash(po.po_number)} />
            <InfoCell label="Work Order" value={dash(po.work_order)} />
            <InfoCell label="Customer Name" value={dash(po.customer_name)} />
            <InfoCell label="Assigned To" value={assignedToText} />
            <InfoCell label="Ship Date" value={formatDate(po.ship_date)} />
          </View>
        </View>

        {/* Inspection Status */}
        <SectionBar title="INSPECTION STATUS" />
        <View style={s.statusGrid} wrap={false}>
          {inspectionStatuses.map(item => (
            <View key={item.key} style={s.statusBox}>
              <Text style={s.statusBoxLabel}>{item.label}</Text>
              <View style={s.statusBoxRow}>
                <View style={[s.statusDot, { backgroundColor: inspectionStatusColor(item.status) }]} />
                <Text style={[s.statusBoxValue, { color: inspectionStatusColor(item.status) }]}>{item.status}</Text>
              </View>
            </View>
          ))}
        </View>

        {/* Order Summary */}
        <SectionBar title="ORDER SUMMARY" />
        <View style={s.card} wrap={false}>
          <View style={s.infoGrid}>
            <InfoCell label="Total SKUs" value={String(skus.length)} />
            <InfoCell label="Total Packs" value={formatPacks(totalPacks)} />
            <InfoCell label="Total Units" value={totalUnits.toLocaleString()} />
          </View>
        </View>

        {/* SKU Details */}
        <SectionBar title="SKU DETAILS" badge={`${skus.length} ITEMS`} />

        {skus.length === 0 ? (
          <Text style={s.noDataText}>No SKUs found for this purchase order.</Text>
        ) : (
          <View style={s.table}>
            <View style={s.tableHead} wrap={false}>
              {vc.sku_number && <Text style={[s.th, s.colSku]}>SKU Number</Text>}
              {vc.image && <Text style={[s.th, s.colImage]}>Image</Text>}
              {vc.category && <Text style={[s.th, s.colCategory]}>Category</Text>}
              {vc.vendor && <Text style={[s.th, s.colVendor]}>Vendor Code</Text>}
              {vc.description && <Text style={[s.th, s.colDesc]}>Description</Text>}
              {vc.assigned_to && <Text style={[s.th, s.colAssigned]}>Assigned To</Text>}
              {vc.inspected_by && <Text style={[s.th, s.colInspected]}>Inspected By</Text>}
              {vc.inspection_date && <Text style={[s.th, s.colDate]}>Inspection Date</Text>}
              {vc.packs && <Text style={[s.th, s.colPacks]}>Packs</Text>}
              {vc.qty_per_pack && <Text style={[s.th, s.colQtyPack]}>Qty/Pack</Text>}
              {vc.total_qty && <Text style={[s.th, s.colQty]}>Total Qty</Text>}
            </View>
            {skus.map((sku, idx) => {
              const descExtra = [sku.color && `Color: ${sku.color}`, sku.size && `Size: ${sku.size}`].filter(Boolean).join(' | ');
              const packs = (sku.qty_per_pack || 0) > 0 ? (sku.total_qty || 0) / (sku.qty_per_pack || 1) : 0;
              const assignedTo = getSkuAssignedTo(sku);
              return (
                <View key={sku.id} style={[s.tableRow, idx % 2 === 1 ? s.tableRowAlt : {}]} wrap={false}>
                  {vc.sku_number && <Text style={[s.tdBold, s.colSku]}>{dash(sku.sku_number)}</Text>}
                  {vc.image && (
                    <View style={[s.tdImgCell, s.colImage]}>
                      {sku.image_url ? (
                        <Image src={sku.image_url} style={s.thumb} />
                      ) : (
                        <View style={s.thumbPlaceholder} />
                      )}
                    </View>
                  )}
                  {vc.category && <Text style={[s.td, s.colCategory]}>{dash(sku.product_category)}</Text>}
                  {vc.vendor && <Text style={[s.td, s.colVendor]}>{dash(sku.vendor_code)}</Text>}
                  {vc.description && (
                    <View style={[{ paddingHorizontal: 4, paddingVertical: 6 }, s.colDesc]}>
                      <Text style={{ fontSize: 6.8, color: '#334155' }}>{dash(sku.description)}</Text>
                      {descExtra ? <Text style={{ fontSize: 5.8, color: '#94a3b8', marginTop: 1 }}>{descExtra}</Text> : null}
                    </View>
                  )}
                  {vc.assigned_to && <Text style={[assignedTo === 'Unassigned' ? s.tdMuted : s.td, s.colAssigned]}>{assignedTo}</Text>}
                  {vc.inspected_by && <Text style={[sku.last_inspection?.inspector_name ? s.td : s.tdMuted, s.colInspected]}>{dash(sku.last_inspection?.inspector_name)}</Text>}
                  {vc.inspection_date && <Text style={[sku.last_inspection?.completed_at ? s.td : s.tdMuted, s.colDate]}>{sku.last_inspection?.completed_at ? formatDateTime(sku.last_inspection.completed_at) : '—'}</Text>}
                  {vc.packs && <Text style={[s.td, s.colPacks]}>{packs > 0 ? formatPacks(packs) : '—'}</Text>}
                  {vc.qty_per_pack && <Text style={[s.td, s.colQtyPack]}>{sku.qty_per_pack || '—'}</Text>}
                  {vc.total_qty && <Text style={[s.tdBold, s.colQty]}>{(sku.total_qty || 0).toLocaleString()}</Text>}
                </View>
              );
            })}
            <View style={s.totalsRow} wrap={false}>
              {totalsLabelFlex > 0 && <Text style={[s.totalsLabel, { flex: totalsLabelFlex }]}>TOTAL</Text>}
              {vc.packs && <Text style={[s.tdBold, s.colPacks, { color: '#1d4ed8' }]}>{formatPacks(totalPacks)}</Text>}
              {vc.qty_per_pack && <Text style={[s.tdBold, s.colQtyPack]}> </Text>}
              {vc.total_qty && <Text style={[s.tdBold, s.colQty, { color: '#1d4ed8' }]}>{totalUnits.toLocaleString()}</Text>}
            </View>
          </View>
        )}

        <PageFooter />
      </Page>
    </Document>
  );
};

export default PurchaseOrderPDF;
